import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { FadeInOut } from '@app/utils/angular-animations';
import { dataSizeUnitToBytes } from '@app/utils/data-size-unit-to-bytes';
import { FileChecker, RejectedFiles } from '@app/utils/file-checker';
import { Key } from '@app/utils/key';
import { stoppableEventHandler } from '@app/utils/stoppable-event-handler';

@Component({
  selector: 'portal-file-drop',
  templateUrl: './file-drop.component.html',
  styles: [],
  animations: [FadeInOut],
  standalone: false,
})
export class FileDropComponent implements OnDestroy, OnInit {
  @Input()
  accept: string = '';

  /**
   * i18n key that is displayed, if a given file is not accepted because of its type
   */
  @Input()
  acceptDescription?: string;

  @Input()
  hasFile: boolean = false;

  @Input()
  set maxFileSize(value: number | string) {
    if (value != null) {
      this.maxFileSizeInBytes = dataSizeUnitToBytes(value);
      if (this.fileChecker != null) {
        this.fileChecker.setMaxFileSize(this.maxFileSizeInBytes);
      }
    }
  }

  @Input()
  multiple: boolean = false;

  @Input()
  variant: 'plain' | 'full-size' = 'full-size';

  @Output()
  dragEnter = new EventEmitter<DataTransfer>();

  @Output()
  dragLeave = new EventEmitter<DataTransfer>();

  @Output()
  dropped = new EventEmitter<File[]>();

  @Output()
  rejected = new EventEmitter<RejectedFiles>();

  @ContentChildren(TranscludeDirective)
  transclusions: QueryList<TranscludeDirective>;

  showDropArea: boolean = false;
  warning: string | null = null;

  private dragAndDropActive: boolean = false;
  private fileChecker!: FileChecker;
  private hoveringOverDropArea: boolean = false;
  private maxFileSizeInBytes?: number;

  private eventHandlers = {
    /**
     * Handler that starts the necessary events if the user drags files over the document
     */
    dragenter: stoppableEventHandler(document.body, 'dragenter', (event: DragEvent) => {
      this.dragAndDropActive = true;

      if (this.containsFiles(event)) {
        this.toggleDropArea(true);

        if (this.multiple === false && event.dataTransfer.items.length > 1) {
          this.warning = 'multiple';
        }

        this.eventHandlers.dragenter.stop();
        this.eventHandlers.keydown.start();

        if (this.variant !== 'full-size') {
          this.eventHandlers.dragleave.start();
        }
      }
    }),

    /**
     * If using the plain variant, we need a dragleave handler that gets called
     * if the user is not hovering over the drop area.
     */
    dragleave: stoppableEventHandler(document.body, 'dragleave', (event: DragEvent) => {
      if (this.containsFiles(event)) {
        this.eventHandlers.dragenter.start();
      }
    }),

    /**
     * Handler that checks if the user presses escape during a drag operation.
     * If so, the handler will stop the event because it could be that the user
     * is currently in a dialog and hitting escape would close the dialog.
     */
    keydown: stoppableEventHandler(document.body, 'keydown', (event: KeyboardEvent) => {
      if (event.key === Key.Escape && this.dragAndDropActive) {
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
      }

      this.eventHandlers.keydown.stop();
    }),

    stop: () => {
      this.eventHandlers.dragenter.stop();
      this.eventHandlers.dragleave.stop();
      this.eventHandlers.keydown.stop();
    },
  };

  ngOnDestroy(): void {
    this.eventHandlers.stop();
  }

  ngOnInit(): void {
    this.eventHandlers.dragenter.start();

    this.fileChecker = new FileChecker({
      accept: this.accept,
      maxFileSize: this.maxFileSizeInBytes,
    });
  }

  handleDragLeave(event: DragEvent): void {
    if (this.containsFiles(event)) {
      this.dragLeave.emit(event.dataTransfer);

      this.dragAndDropActive = false;
      this.hoveringOverDropArea = false;

      this.toggleDropArea(false);
    }

    this.eventHandlers.dragenter.start();
  }

  handleDragOver(event: DragEvent): void {
    if (this.containsFiles(event)) {
      event.preventDefault();

      if (this.hoveringOverDropArea === false) {
        this.hoveringOverDropArea = true;
        this.dragEnter.emit(event.dataTransfer);
      }

      if (this.warning != null) {
        event.dataTransfer.dropEffect = 'none';
      } else {
        event.dataTransfer.dropEffect = 'copy';
      }
    }
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();

    const fileList = event.dataTransfer?.files;

    this.dragAndDropActive = false;
    this.hoveringOverDropArea = false;

    if (fileList != null) {
      const { rejectedFiles, acceptedFiles } = this.fileChecker.check(fileList);

      if (rejectedFiles.length > 0) {
        this.rejected.emit({
          accept: this.acceptDescription ?? this.accept,
          files: rejectedFiles,
          maxFileSize: this.maxFileSizeInBytes,
        });
      }

      if (acceptedFiles.length > 0) {
        this.dropped.emit(acceptedFiles);
      }
    }

    this.eventHandlers.dragenter.start();
    this.eventHandlers.keydown.stop();
    this.toggleDropArea(false);
  }

  /**
   * Check if the drag event contains files and if so then prevent the default action
   * @param event
   */
  private containsFiles(event: DragEvent): boolean {
    const containsFiles = !!event.dataTransfer?.types?.reduce((a, b) => a || b === 'Files', false);

    if (containsFiles) {
      event.preventDefault();
    }

    return containsFiles;
  }

  private toggleDropArea(show?: boolean): void {
    if (show == null) {
      this.showDropArea = !this.showDropArea;
    } else {
      this.showDropArea = show;
    }

    this.warning = null;
  }
}
